import React, { useEffect, useState } from "react";
import { collection, getDocs, orderBy, query, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import Footer from "../components/Footer";
import "../styles/AdminLogs.css";

interface Log {
  id: string;
  type: string;
  user: string;
  target: string;
  timestamp?: { seconds: number; nanoseconds: number };
  isAdminAction?: boolean;
}

interface LogWithRole extends Log {
  userRole?: string;
}

const ITEMS_PER_PAGE = 25;

const AdminLogs: React.FC = () => {
  const [logs, setLogs] = useState<LogWithRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterUser, setFilterUser] = useState("");
  const [filterAction, setFilterAction] = useState("");
  const [filterDate, setFilterDate] = useState("");

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        // Récupérer les logs administrateur
        const adminQuery = query(collection(db, "AdminLogs"), orderBy("timestamp", "desc"));
        const adminSnapshot = await getDocs(adminQuery);

        // Récupérer les logs utilisateurs
        const userQuery = query(collection(db, "UserLogs"), orderBy("timestamp", "desc"));
        const userSnapshot = await getDocs(userQuery);

        const logsData: LogWithRole[] = [];

        // Récupérer les rôles de tous les utilisateurs uniques pour éviter les lectures répétées
        const uniqueUserEmails = new Set<string>();
        adminSnapshot.forEach(docSnap => uniqueUserEmails.add(docSnap.data().admin));
        userSnapshot.forEach(docSnap => uniqueUserEmails.add(docSnap.data().user));

        const userRoles = new Map<string, string>();
        for (const email of uniqueUserEmails) {
          if (email) { // Vérifier que l'email n'est pas vide
            const userDocRef = doc(db, "Users", email.trim().toLowerCase());
            const userDocSnap = await getDoc(userDocRef);
            if (userDocSnap.exists()) {
              userRoles.set(email, userDocSnap.data()?.role || "non défini");
            }
          }
        }

        // Ajouter les logs administrateur avec le rôle
        adminSnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          const userEmail = data.admin;
          logsData.push({
            id: docSnap.id,
            type: data.type,
            user: userEmail,
            target: data.target,
            timestamp: data.timestamp,
            isAdminAction: true,
            userRole: userRoles.get(userEmail) || "non défini"
          });
        });

        // Ajouter les logs utilisateurs avec le rôle
        userSnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          const userEmail = data.user;
          logsData.push({
            id: docSnap.id,
            type: data.type,
            user: userEmail,
            target: data.target,
            timestamp: data.timestamp,
            isAdminAction: false,
            userRole: userRoles.get(userEmail) || "non défini"
          });
        });

        // Trier tous les logs par date
        logsData.sort((a, b) => {
          if (!a.timestamp || !b.timestamp) return 0;
          return b.timestamp.seconds - a.timestamp.seconds;
        });

        setLogs(logsData);
      } catch (error) {
        console.error("Erreur chargement logs :", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, []);

  const formatDate = (timestamp?: { seconds: number }) => {
    if (!timestamp) return "–";
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleString("fr-FR");
  };

  // Filtrer les logs
  const filteredLogs = logs.filter(log => {
    const matchUser = filterUser ? log.user.toLowerCase().includes(filterUser.toLowerCase()) : true;
    const matchAction = filterAction ? log.type.toLowerCase().includes(filterAction.toLowerCase()) : true;
    const matchDate = filterDate ? formatDate(log.timestamp).includes(filterDate) : true;
    return matchUser && matchAction && matchDate;
  });

  // Calculer les logs à afficher pour la page courante
  const indexOfLastLog = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstLog = indexOfLastLog - ITEMS_PER_PAGE;
  const currentLogs = filteredLogs.slice(indexOfFirstLog, indexOfLastLog);
  const totalPages = Math.ceil(filteredLogs.length / ITEMS_PER_PAGE);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const clearFilters = () => {
    setFilterUser("");
    setFilterAction("");
    setFilterDate("");
    setCurrentPage(1);
  };

  const getBadgeClass = (userRole: string, isAdminAction: boolean) => {
    if (isAdminAction) return "admin-logs-badge admin-logs-badge-admin";
    switch (userRole) {
      case 'admin': return "admin-logs-badge admin-logs-badge-admin";
      case 'foodinnov': return "admin-logs-badge admin-logs-badge-foodinnov";
      case 'regeexport': return "admin-logs-badge admin-logs-badge-regeexport";
      default: return "admin-logs-badge admin-logs-badge-default";
    }
  };

  const getTypeBadgeClass = (isAdminAction: boolean) => {
    return isAdminAction ? "admin-logs-badge admin-logs-badge-admin" : "admin-logs-badge admin-logs-badge-user";
  };

  return (
    <div className="admin-logs-container">
      <h2>Historique des actions</h2>

      {/* Filtres */}
      <div className="admin-logs-filters">
        <input
          type="text"
          placeholder="Filtrer par utilisateur..."
          value={filterUser}
          onChange={(e) => setFilterUser(e.target.value)}
        />
        <input
          type="text"
          placeholder="Filtrer par action..."
          value={filterAction}
          onChange={(e) => setFilterAction(e.target.value)}
        />
        <input
          type="text"
          placeholder="Filtrer par date..."
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
        />
        <button
          onClick={clearFilters}
          className="admin-logs-clear-filters"
        >
          Effacer les filtres
        </button>
        <span className="admin-logs-count">
          {filteredLogs.length} action(s) trouvée(s)
        </span>
      </div>

      {loading ? (
        <p className="admin-logs-loading">Chargement...</p>
      ) : filteredLogs.length === 0 ? (
        <p className="admin-logs-empty">Aucune action enregistrée.</p>
      ) : (
        <>
          <div className="admin-logs-table-container">
            <table className="admin-logs-table">
              <thead>
                <tr>
                  <th style={{ width: "10%" }}>Date</th>
                  <th style={{ width: "20%" }}>Utilisateur</th>
                  <th style={{ width: "8%" }}>Rôle</th>
                  <th style={{ width: "17%" }}>Action</th>
                  <th style={{ width: "35%" }}>Cible</th>
                  <th style={{ width: "10%" }}>Type</th>
                </tr>
              </thead>
              <tbody>
                {currentLogs.map((log) => (
                  <tr key={log.id}>
                    <td className="admin-logs-date">{formatDate(log.timestamp)}</td>
                    <td className="admin-logs-user">{log.user}</td>
                    <td>
                      <span className={getBadgeClass(log.userRole || "", log.isAdminAction || false)}>
                        {log.userRole || (log.isAdminAction ? "Administrateur" : "Utilisateur")}
                      </span>
                    </td>
                    <td className="admin-logs-action">{log.type}</td>
                    <td className="admin-logs-target">{log.target}</td>
                    <td>
                      <span className={getTypeBadgeClass(log.isAdminAction || false)}>
                        {log.isAdminAction ? "Admin" : "Utilisateur"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="admin-logs-pagination">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Précédent
            </button>
            <span className="admin-logs-pagination-info">
              Page {currentPage} sur {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Suivant
            </button>
          </div>
        </>
      )}

      <Footer />
    </div>
  );
};

export default AdminLogs;
